import { assertNever } from "owa-assert";
import type { MailboxInfo } from "owa-client-types";
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from "owa-feature-flags";
import { OcpsPolicyFeature } from "../store/schema/OutlookCloudPolicyTypes";
import { isPoliciesEnabledForAccount } from "./isPoliciesEnabledForAccount";

type FlightCheck = (mailboxInfo: MailboxInfo) => boolean;

interface OcpsPolicyFeatureId {
    // CPS featureId (`<catalog>;L_<Feature>`) as the service knows it.
    featureId: string;
    isEnabled: FlightCheck;
}

// Some policy features also require the restart-after-policy-change flight. Others rely only on
// their own rollout flight.
const isShowAutoCompleteListFlightEnabled: FlightCheck = () => isGlobalFeatureEnabled("mon-recipient-hide-picker");

const isPrivacyFlightEnabled: FlightCheck = () => isGlobalFeatureEnabled("policy-ocps-owsprime-privacy");

const isUnifiedInboxFlightEnabled: FlightCheck = () => isGlobalFeatureEnabled("acct-unifiedInbox");

const isKeyboardStylesFlightEnabled: FlightCheck = mailboxInfo => isAccountFeatureEnabled("policy-restartAfterPoliciesChange", mailboxInfo);

const isFocusedInboxFlightEnabled: FlightCheck = mailboxInfo => isAccountFeatureEnabled("policy-focusedInbox", mailboxInfo) &&
    isAccountFeatureEnabled("policy-restartAfterPoliciesChange", mailboxInfo);

const isPresenceFlightEnabled: FlightCheck = () => isGlobalFeatureEnabled("mon-presence-policy-checks");

const isNotificationsFlightEnabled: FlightCheck = mailboxInfo => isGlobalFeatureEnabled("policy-notificationPolicies") &&
    isAccountFeatureEnabled("policy-restartAfterPoliciesChange", mailboxInfo);

const isAtMentionsFlightEnabled: FlightCheck = () => isGlobalFeatureEnabled("mon-at-mentions-setting");

// A client feature can map to several CPS featureIds (privacy alone owns four), so featureIds are
// listed flat with the flight that gates them rather than derived from the settingId map.
const ocpsPolicyFeatureIds: OcpsPolicyFeatureId[] = [
    {
        featureId: "outlk16;L_SuggestnameswhilecompletingToCcandBccfields",
        isEnabled: isShowAutoCompleteListFlightEnabled,
    },
    { featureId: "office16;L_OptionalConnectedExperiences", isEnabled: isPrivacyFlightEnabled },
    { featureId: "office16;L_ConnectedOfficeExperiences", isEnabled: isPrivacyFlightEnabled },
    // Beware of the typo "Anlayzing" - this is how the server stores it
    {
        featureId: "office16;L_OfficeExperiencesAnlayzingContent",
        isEnabled: isPrivacyFlightEnabled,
    },
    {
        featureId: "office16;L_OfficeExperiencesDownloadingContent",
        isEnabled: isPrivacyFlightEnabled,
    },
    { featureId: "outlooknew;L_ShowAllAccountsView", isEnabled: isUnifiedInboxFlightEnabled },
    { featureId: "outlooknew;L_KeyboardStyles", isEnabled: isKeyboardStylesFlightEnabled },
    { featureId: "outlooknew;L_AlwaysShowBcc", isEnabled: () => true },
    {
        featureId: "outlooknew;L_DefaultReadingPaneReplyBehavior",
        isEnabled: () => true,
    },
    { featureId: "outlooknew;L_FocusedInbox", isEnabled: isFocusedInboxFlightEnabled },
    { featureId: "office16;L_TurnOffPresenceIntegration", isEnabled: isPresenceFlightEnabled },
    { featureId: "office16;L_ConfigurePresenceIcons", isEnabled: isPresenceFlightEnabled },
    { featureId: "outlooknew;L_NotificationSettings", isEnabled: isNotificationsFlightEnabled },
    { featureId: "outlooknew;L_MentionNamesWithAtSymbol", isEnabled: isAtMentionsFlightEnabled },
];

/**
 * Returns true when every flight gating an OCPS policy feature is on. The store uses this to decide
 * whether policy data is applicable, so consumers never check the rollout flight themselves.
 */
export function isOcpsPolicyFeatureEnabled(feature: OcpsPolicyFeature, mailboxInfo: MailboxInfo): boolean {
    if (!isPoliciesEnabledForAccount(mailboxInfo)) {
        return false;
    }
    
    switch (feature) {
        case OcpsPolicyFeature.ShowAutoCompleteList:
            return isShowAutoCompleteListFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.Privacy:
            return isPrivacyFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.UnifiedInbox:
            return isUnifiedInboxFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.KeyboardStyles:
            return isKeyboardStylesFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.AlwaysShowBcc:
        case OcpsPolicyFeature.ReplyBehavior:
            return true;
        case OcpsPolicyFeature.FocusedInbox:
            return isFocusedInboxFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.Presence:
            return isPresenceFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.Notifications:
            return isNotificationsFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.AtMentions:
            return isAtMentionsFlightEnabled(mailboxInfo);
        default:
            return assertNever(feature);
    }
}

/**
 * The CPS featureIds the app consumes with their flights turned on for the account. OWS Prime uses
 * this list to scope policy change detection to what the app actually reads.
 */
export function getEnabledOcpsPolicyFeatureIds(mailboxInfo: MailboxInfo): string[] {
    return ocpsPolicyFeatureIds
        .filter(({ isEnabled }) => isEnabled(mailboxInfo))
        .map(({ featureId }) => featureId);
}
